import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { setAllFarmDevices, setFarmDevice } from '../../../../actions/farmSectorActions';
import CustomButton from '../../../../components/General/CustomButton';
import CustomConfirmationModal from '../../../../components/General/CustomConfirmationModal';
import CustomHeader from '../../../../components/General/CustomHeader';
import Page from '../../../../components/Layout/Page';
import useFetchApi from '../../../../hooks/useFetchApi';
import apiClient from '../../../../requests/api/apiClient';
import { GetSensorModelByIdResponse, SensorSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { FetchError } from '../../../types';
import ImagesContainer from './component/ImagesContainer';
import useStyles from './styles';

const SensorModelDetail = () => {
  const classes = useStyles();
  const { id: sensorModelId } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSensorModalOpen, setDeleteSensorModalOpen] = useState(false);
  const [sensorToDelete, setSensorToDelete] = useState<string | null>(null);
  const [refreshDevice, setRefreshDevice] = useState(false);
  const { allFarmDevices } = useSelector((state: ReduxState) => state.farmSector);
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);

  const { data: fetchData, loading: isLoading } = useFetchApi<GetSensorModelByIdResponse>({
    modelEndpoint: 'sensorModelsDetail',
    modelName: 'sensorModels',
    id: sensorModelId,
    dependencies: [refreshDevice],
  });

  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);
  const [isSendingTicket, setIsSendingTicket] = useState(false);
  const [editableName, setEditableName] = useState('');
  const [editableSector, setEditableSector] = useState('');
  const [editableSensorType, setEditableSensorType] = useState('');
  const [editableNodeId, setEditableNodeId] = useState('');
  const [editableIsWeatherStation, setEditableIsWeatherStation] = useState(false);
  const [editableZoneId, setEditableZoneId] = useState('');
  const [editableType, setEditableType] = useState('');
  const [nodes, setNodes] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [localSensors, setLocalSensors] = useState<Record<string, Partial<SensorSchema>>>({});
  const [savingSensors, setSavingSensors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (fetchData?.sensorModel) {
      setEditableName(fetchData.sensorModel.name || '');
      // eslint-disable-next-line no-underscore-dangle
      setEditableSector(fetchData.sensorModel.sector?.id || fetchData.sensorModel.sector?._id || '');
      setEditableSensorType(fetchData.sensorModel.sensorType || '');
      // eslint-disable-next-line no-underscore-dangle
      setEditableNodeId(fetchData.sensorModel.node?._id || '');
      setEditableIsWeatherStation(fetchData.sensorModel.isWeatherStation || false);
      setEditableZoneId(fetchData.sensorModel.zoneId || '');
      setEditableType(fetchData.sensorModel.type || '');
    }
  }, [fetchData]);

  useEffect(() => {
    const getNodesByFarmId = async () => {
      if (selectedFarm?.id) {
        try {
          const response = await apiClient.nodes.nodesByFarmId(selectedFarm.id);
          setNodes(response?.data?.nodes ?? []);
        } catch (error) {
          console.error('Error fetching nodes:', error);
        }
      }
    };
    getNodesByFarmId();
  }, [selectedFarm]);

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const sensorModel = fetchData?.sensorModel;
      const updateData: any = {};

      const assignIfChanged = (field: string, value: any, originalValue: any) => {
        if (value !== originalValue && value !== '') {
          updateData[field] = value;
        }
      };

      assignIfChanged('name', editableName, sensorModel?.name);
      // eslint-disable-next-line no-underscore-dangle
      const originalSectorId = sensorModel?.sector?.id || (sensorModel?.sector as any)?._id || '';
      if (editableSector !== originalSectorId && editableSector !== '') {
        updateData.sector = editableSector;
      }
      assignIfChanged('sensorType', editableSensorType, sensorModel?.sensorType);

      // eslint-disable-next-line no-underscore-dangle
      const originalNodeId = sensorModel?.node?._id || '';
      if (editableNodeId !== originalNodeId && editableNodeId !== '') {
        updateData.nodeId = editableNodeId;
      }

      assignIfChanged('isWeatherStation', editableIsWeatherStation, sensorModel?.isWeatherStation);
      assignIfChanged('zoneId', editableZoneId, sensorModel?.zoneId);
      assignIfChanged('type', editableType, sensorModel?.type);

      if (Object.keys(updateData).length === 0) {
        enqueueSnackbar('No hay cambios para guardar', { variant: 'info' });
        return;
      }

      await apiClient.sensorModels.sensorModelsUpdate(updateData, sensorModelId);
      enqueueSnackbar('Cambios guardados exitosamente', { variant: 'success' });
      setRefreshDevice((prev) => !prev);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSensor = async () => {
    if (!sensorToDelete) return;
    try {
      await apiClient.sensors.sensorsDelete(sensorToDelete);
      enqueueSnackbar('Sensor eliminado exitosamente', { variant: 'success' });
      setDeleteSensorModalOpen(false);
      setSensorToDelete(null);
      setRefreshDevice((prev) => !prev);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleUpdateSensor = async (id: string) => {
    const updatedData = localSensors[id];
    if (!updatedData) return;

    try {
      setSavingSensors((prev) => ({ ...prev, [id]: true }));
      await apiClient.sensors.sensorsUpdate(updatedData, id);
      enqueueSnackbar('Sensor actualizado exitosamente', { variant: 'success' });
      setRefreshDevice((prev) => !prev);
      // Remove from localSensors after successful save
      setLocalSensors((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setSavingSensors((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleLocalSensorChange = (id: string, field: string, value: any) => {
    setLocalSensors((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSendSupportTicket = async () => {
    try {
      setIsSendingTicket(true);
      const sectorName = selectedFarm?.sectors?.find((s) => s.id === editableSector)?.name || '';
      await apiClient.supportTicket.send({
        accountName: selectedAccount?.name || '',
        farmName: selectedFarm?.name || '',
        deviceName: fetchData?.sensorModel?.name || sensorModelId,
        deviceType: 'Equipo',
        sectorName,
        senderName: `${currentUser?.name || ''} ${currentUser?.lastname || ''}`.trim(),
        senderEmail: currentUser?.email || '',
      });
      enqueueSnackbar('Ticket enviado a soporte exitosamente', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error al enviar el ticket de soporte', { variant: 'error' });
    } finally {
      setIsSendingTicket(false);
    }
  };

  const handleDeleteDevice = async () => {
    try {
      await apiClient.sensorModels.sensorModelsDelete(sensorModelId);
      enqueueSnackbar('Sensor eliminado con éxito', { variant: 'success' });
      setDeleteModalOpen(false);
      const allFarmDevicesFiltered = allFarmDevices.filter(
        (sensorModel) => sensorModel.id !== sensorModelId,
      );
      dispatch(setAllFarmDevices(allFarmDevicesFiltered));
      dispatch(setFarmDevice(''));
      history.push('/layout');
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'Operative': return `${classes.statusBadge} ${classes.operativeStatus}`;
      case 'Offline': return `${classes.statusBadge} ${classes.offlineStatus}`;
      case 'Repair': return `${classes.statusBadge} ${classes.repairStatus}`;
      default: return classes.statusBadge;
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case 'Operative': return 'Operativo';
      case 'Offline': return 'Fuera de línea';
      case 'Repair': return 'Reparación';
      default: return status;
    }
  };

  if (isLoading && !fetchData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Page className={classes.root} title="Detalle Equipo">
      <Container maxWidth={false} className={classes.containerContainer}>
        <CustomHeader
          title={`${selectedFarm?.name || ''} / ${fetchData?.sensorModel?.name || ''}`}
          showBackButton={true}
        />

        <Box display="flex" flexDirection="column" gap={3} mt={2}>
          {/* Información Section */}
          <Card className={classes.infoCard}>
            <CardContent>
              <Typography variant="h4" gutterBottom>Información</Typography>
              <Box className={classes.infoRow} mt={2}>
                <Box className={classes.infoItem}>
                  <TextField
                    label="Nombre equipo"
                    value={editableName}
                    onChange={(e) => setEditableName(e.target.value)}
                    fullWidth
                    variant="standard"
                  />
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Sector"
                    value={editableSector}
                    onChange={(e) => setEditableSector(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    {selectedFarm?.sectors?.map((sector) => (
                      <MenuItem key={sector.id} value={sector.id}>
                        {sector.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Tipo sensor"
                    value={editableSensorType}
                    onChange={(e) => setEditableSensorType(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    <MenuItem value="Suelo">Suelo</MenuItem>
                    <MenuItem value="Clima">Clima</MenuItem>
                    <MenuItem value="Planta">Planta</MenuItem>
                    <MenuItem value="Riego">Riego</MenuItem>
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Nodo asociado"
                    value={editableNodeId}
                    onChange={(e) => setEditableNodeId(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    {nodes.map((node) => (
                      <MenuItem key={node.id} value={node.id}>
                        {node.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Estación de clima"
                    value={editableIsWeatherStation ? 'si' : 'no'}
                    onChange={(e) => setEditableIsWeatherStation(e.target.value === 'si')}
                    fullWidth
                    variant="standard"
                  >
                    <MenuItem value="si">Si</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    label="Zone ID"
                    value={editableZoneId}
                    onChange={(e) => setEditableZoneId(e.target.value)}
                    fullWidth
                    variant="standard"
                  />
                </Box>
                <Box className={classes.infoItem}>
                  <TextField
                    select
                    label="Tipo"
                    value={editableType}
                    onChange={(e) => setEditableType(e.target.value)}
                    fullWidth
                    variant="standard"
                  >
                    <MenuItem value="sonda">Sonda</MenuItem>
                    <MenuItem value="sensor">Sensor</MenuItem>
                  </TextField>
                </Box>
                <Box className={classes.infoItem} display="flex" alignItems="center" pt={2}>
                  <Typography variant="body2" color="textSecondary">
                    Estado:
                    <span className={getStatusStyle(fetchData?.sensorModel?.status || 'Offline')}>
                      {translateStatus(fetchData?.sensorModel?.status || 'Offline')}
                    </span>
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" mt={5} gap={2} flexWrap="wrap">
                {fetchData?.sensorModel?.status === 'Offline' && (
                  <CustomButton
                    buttonText="Reportar equipo offline"
                    color="blue"
                    onClick={handleSendSupportTicket}
                    disabled={isSendingTicket}
                    setLoadingSpinnerWhenDisabled={true}
                    style={{ width: '250px' }}
                  />
                )}
                <CustomButton
                  buttonText="Guardar cambios"
                  color="blue"
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  setLoadingSpinnerWhenDisabled={true}
                  style={{ width: '250px' }}
                />
                <CustomButton
                  buttonText="Eliminar Equipo"
                  color="red"
                  onClick={() => setDeleteModalOpen(true)}
                  style={{ width: '250px' }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Sensores Table Section */}
          <Card className={classes.tableCard}>
            <CardContent>
              <TableContainer component={Box}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeader}>Nombre sensor</TableCell>
                      <TableCell className={classes.tableHeader}>Wiseconn ID</TableCell>
                      <TableCell className={classes.tableHeader}>Tipo sensor</TableCell>
                      <TableCell className={classes.tableHeader}>Estado</TableCell>
                      <TableCell className={classes.tableHeader}>Última actividad</TableCell>
                      <TableCell className={classes.tableHeader} align="center">Activo</TableCell>
                      <TableCell className={classes.tableHeader} align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fetchData?.sensorModel?.sensors?.map((sensor: SensorSchema) => {
                      // eslint-disable-next-line no-underscore-dangle
                      const sensorId = sensor._id;
                      const currentName = localSensors[sensorId]?.name ?? sensor.name;
                      const currentWiseconnId = localSensors[sensorId]?.wiseconnId ?? sensor.wiseconnId ?? '';
                      const currentActive = localSensors[sensorId]?.active
                        ?? sensor.active ?? true;
                      const isSavingRow = savingSensors[sensorId];

                      return (
                        <TableRow key={sensorId}>
                          <TableCell>
                            <TextField
                              value={currentName}
                              onChange={(e) => handleLocalSensorChange(sensorId, 'name', e.target.value)}
                              fullWidth
                              variant="standard"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={currentWiseconnId}
                              onChange={(e) => handleLocalSensorChange(sensorId, 'wiseconnId', e.target.value)}
                              fullWidth
                              variant="standard"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <span className={getStatusStyle(sensor?.sensorType || '')}>
                              {sensor.sensorType}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={getStatusStyle(sensor.status)}>
                              {translateStatus(sensor.status)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="textSecondary">
                              {sensor.lastActivity ? dayjs(sensor.lastActivity).format('DD/MM/YYYY HH:mm') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={currentActive}
                              onChange={(e) => {
                                const newValue = e.target.checked;
                                handleLocalSensorChange(sensorId, 'active', newValue);
                                // For switches, we might want to save immediately
                                setLocalSensors((prev) => {
                                  const updated = {
                                    ...prev,
                                    [sensorId]: { ...prev[sensorId], active: newValue },
                                  };
                                  // Call update immediately for switch
                                  handleUpdateSensor(sensorId);
                                  return updated;
                                });
                              }}
                              color="primary"
                              disabled={isSavingRow}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {!isSavingRow && (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleUpdateSensor(sensorId)}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSensorToDelete(sensorId);
                                    setDeleteSensorModalOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            {isSavingRow && <CircularProgress size={20} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!fetchData?.sensorModel?.sensors
                      || fetchData.sensorModel.sensors.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">No hay sensores asociados</TableCell>
                        </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Imagenes Section */}
          <Card className={classes.imagesCard}>
            <CardContent>
              {sensorModelId ? (
                <ImagesContainer deviceId={sensorModelId} />
              ) : (
                <Typography>Cargando imágenes...</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>

      <CustomConfirmationModal
        open={deleteModalOpen}
        title="Eliminar Equipo"
        message="¿Estás seguro de que quieres eliminar este equipo?"
        onConfirm={handleDeleteDevice}
        onCancel={() => setDeleteModalOpen(false)}
        confirmButtonColor="delete"
        confirmText="Eliminar"
      />

      <CustomConfirmationModal
        open={deleteSensorModalOpen}
        title="Eliminar Sensor"
        message="¿Estás seguro de que quieres eliminar este sensor?"
        onConfirm={handleDeleteSensor}
        onCancel={() => {
          setDeleteSensorModalOpen(false);
          setSensorToDelete(null);
        }}
        confirmButtonColor="delete"
        confirmText="Eliminar"
      />
    </Page>
  );
};

export default SensorModelDetail;
